<template>
  <v-card class="left-vertical-card">
    <div class="d-flex bg-titre">
      <div class="secondary--text align-self-center pl-3 titre-h3 mr-auto">
        {{ titreModal }}
      </div>

      <v-btn
        class="align-self-center"
        text
        @click="onClose"
      >
        <v-icon color="secondary">
          mdi-close
        </v-icon>
      </v-btn>
    </div>
    <div class="d-flex flex-column">
      <BaseStep
        :is-edit-disabled="currentStep <= 1"
        :is-open="currentStep === 1"
        :is-valid="currentStep > 1"
        :step-index="1"
        class="my-5"
        titre="Graine & surface"
        @edit="changeStep(1)"
      >
        <div slot="stepContent">
          <div v-if="currentStep === 1">
            <BaseTreeSelect
              :selected="typeCultureSelectedID"
              :tree="arborescenceTypeCultureFormatee"
              class="tree-select mb-5"
              label="Type de culture"
              searchable
              @change="selectGraine"
            />
            <BaseMessage
              v-if="isDuplicat"
              class="mb-4"
              color="warning"
              message="Le libellé ne peut pas être le même qu'une autre culture."
              text
              type="warning"
            />
            <AgrialInputNumber
              v-model="surface"
              :rules="[rules.positif]"
              label="Surface"
              min="0"
              suffix="ha"
            />
            <div
              v-if="loading.resumeParcellaire"
              class="mention texte-gris-fonce mb-3 d-flex align-center"
            >
              <v-progress-circular
                class="mr-2"
                color="secondary"
                indeterminate
                size="14"
                width="2"
              />
            </div>
            <div
              v-else-if="resumeParcellaire && resumeParcellaire.surfaceEnHectare"
              class="mention texte-gris-fonce mb-3"
            >
              Surface issue de l'import des remontées parcellaires Agrial : {{ resumeParcellaire.surfaceEnHectare | roundNumber(2) }} ha
            </div>
            <div class="d-flex justify-space-around">
              <AgrButton
                class="pa-5"
                outlined
                @click="onClose()"
              >
                Annuler
              </AgrButton>
              <AgrButton
                :disabled="isBtnContinuerStep1Disabled"
                class="pa-5"
                @click="changeStep(2)"
              >
                Continuer
              </AgrButton>
            </div>
          </div>
          <div v-if="currentStep > 1">
            <v-card-text class="texte py-0 texte-gris-fonce">
              {{ typeCultureSelected?.nom }}
            </v-card-text>
            <v-card-text class="texte py-0 texte-gris-fonce">
              Surface : {{ formatNumber(surface, 2) }} ha
            </v-card-text>
          </div>
        </div>
      </BaseStep>
      <v-divider />
      <BaseStep
        :is-edit-disabled="currentStep <= 2"
        :is-open="currentStep === 2"
        :is-valid="currentStep > 2"
        :step-index="2"
        class="my-5"
        titre="Rendement ou poids total"
        @edit="changeStep(2)"
      >
        <div slot="stepContent">
          <div
            v-if="currentStep === 2"
            class="d-flex flex-column"
          >
            <AgrialSwitch
              v-if="isLinFibre"
              v-model="isSaisieFilasse"
              label-off="Paille"
              label-on="Filasse"
              perso-color-valid
            />
            <div class="d-flex justify-space-evenly">
              <AgrialInputNumber
                v-model="rendement"
                :label="labelRendement"
                :max="isSaisieFilasse ? 10000 : 150"
                :rules="rendement != null ? [rules.max, rules.min, rules.positif] : []"
                :suffix="uniteRendement"
                class="input-number mr-1"
                :min="0"
                @input="computePoids"
              />
              <v-icon class="pb-6">
                mdi-arrow-left-right
              </v-icon>
              <AgrialInputNumber
                v-model="poidsEnTonne"
                :label="labelPoids"
                :rules="[rules.positif]"
                :suffix="unitePoids"
                class="input-number ml-1"
                min="0"
                @input="computeRendement"
              />
            </div>
            <div
              v-if="loading.resumeApport"
              class="mb-4 ml-1 mention texte-gris-fonce d-flex align-center"
            >
              <v-progress-circular
                class="mr-2"
                color="secondary"
                indeterminate
                size="14"
                width="2"
              />
              Recherche du poids total de vos apports...
            </div>
            <div
              v-else-if="poidsApporteEnTonne"
              class="mb-4 ml-1 mention texte-gris-fonce"
            >
              Poids total de vos apports ({{ graineRs3Correspondantes }}) : {{ poidsApporteEnTonne }} t
            </div>
            <div class="d-flex justify-space-around">
              <AgrButton
                class="py-5 px-3"
                outlined
                @click="changeStep(1)"
              >
                Annuler
              </AgrButton>
              <AgrButton
                :loading="loading.ajoutCulture"
                class="py-5 px-3"
                outlined
                @click="onSaisirPlusTard"
              >
                Saisir plus tard
              </AgrButton>
              <AgrButton
                :disabled="isBtnContinuerStep2Disabled"
                :loading="loading.ajoutCulture"
                class="py-5 px-3"
                @click="changeStep(3)"
              >
                {{ isLinFibre ? 'Continuer' : 'Valider' }}
              </AgrButton>
            </div>
          </div>
          <div v-if="currentStep > 2">
            <v-card-text class="texte py-0 texte-gris-fonce">
              Rendement {{ isLinFibre ? isSaisieFilasse ? 'filasse' : 'paille' : '' }} : {{ formatNumber(rendement, 3) }} {{ uniteRendement }}
            </v-card-text>
            <v-card-text class="texte py-0 texte-gris-fonce">
              Poids total {{ isLinFibre ? isSaisieFilasse ? 'filasse' : 'paille' : '' }} : {{ formatNumber(poidsEnTonne, 3) }} {{ unitePoids }}
            </v-card-text>
          </div>
        </div>
      </BaseStep>
      <v-divider v-if="isLinFibre" />
      <BaseStep
        v-if="isLinFibre"
        :is-edit-disabled="currentStep <= 3"
        :is-open="currentStep === 3"
        :is-valid="currentStep > 3"
        :step-index="3"
        class="my-5"
        titre="Taux de filasse"
        @edit="changeStep(3)"
      >
        <div slot="stepContent">
          <div v-if="currentStep === 3">
            <AgrialInputNumber
              v-if="isLinFibre"
              v-model="tauxFilasseEnPourcentage"
              class="input-number"
              label="Taux de filasse"
              max="100"
              min="0"
              suffix="%"
            />
            <div class="d-flex justify-space-around">
              <AgrButton
                class="py-5 px-3"
                outlined
                @click="changeStep(2)"
              >
                Annuler
              </AgrButton>
              <AgrButton
                :loading="loading.ajoutCulture"
                class="py-5 px-3"
                outlined
                @click="onSaisirPlusTard"
              >
                Saisir plus tard
              </AgrButton>
              <AgrButton
                :disabled="isBtnContinuerStep3Disabled"
                :loading="loading.ajoutCulture"
                class="py-5 px-3"
                @click="saveCulture"
              >
                Valider
              </AgrButton>
            </div>
          </div>
        </div>
      </BaseStep>
    </div>
    <v-dialog
      v-model="isUpdateModalOpen"
      persistent
      width="500"
    >
      <ConfirmModal
        cancel-text="Non"
        confirm-text="Oui"
        title="Confirmation"
        @cancel="cancelConfirm"
        @confirm="updateConfirm"
      >
        <p>Attention, vous souhaitez modifier le nom de la graine. Si vous continuez, vos données liées à cette graine seront supprimées.</p>
        <p>Voulez-vous vraiment continuer ?</p>
      </ConfirmModal>
    </v-dialog>
  </v-card>
</template>

<script>
import { AgrButton } from '@agrial/ui'
import { mapActions, mapGetters } from 'vuex'
import BaseStep from '@/components/common/base-step.vue'
import BaseTreeSelect from '@/components/common/base-treeselect.vue'
import ConfirmModal from '@/components/modal/confirm-modal.vue'
import {clone} from '@/utils/clone'
import numberFilter from '@/filters/number.filter'
import { enKilo, enTonne, poidsFilasseEnTonne, poidsPailleEnTonne, rendementFilasseEnKiloParHectare, rendementPailleEnQuintauxParHectare } from '@/utils/rendement'
import BaseMessage from '@/components/common/base-message.vue'
import AgrialInputNumber from '@/components/form/agrial-input-number.vue'
import AgrialSwitch from '@/components/form/agrial-switch.vue'
import { round } from 'lodash'
import { trouverNoeud, formaterNoeud } from '@/utils/arborescence'

export default {
  name: 'AjoutCultureModal',
  components: {AgrialSwitch, AgrButton, AgrialInputNumber, BaseMessage, ConfirmModal, BaseTreeSelect, BaseStep},
  props: {
    annee: {
      type: String,
      required: true
    },
    culture: {
      type: Object,
      default: null
    },
    cultures: {
      type: Array,
      required: true
    },
    stepParent: {
      type: Number,
      default: 1
    }
  },

  data () {
    return {
      /*
       * Les clefs doivent etre declarees ici : Vue 2 ne rend pas reactive une
       * propriete ajoutee apres coup a un objet du `data`. `loading.resumeApport`
       * et `loading.resumeParcellaire` etaient poses a la volee par les methodes
       * de chargement, donc invisibles du template.
       */
      loading: {
        ajoutCulture: false,
        resumeApport: false,
        resumeParcellaire: false
      },
      erreur: {
        ajoutCulture: false,
        resumeApport: false,
        resumeParcellaire: false
      },
      lock: {
        poids : false,
        rendement: false
      },
      currentStep: 1,
      cultureModifiable: {},
      typeCultureSelectedID: null,
      surface: null,
      isSaisieFilasse: false,
      rendement: null,
      poidsEnTonne: null,
      tauxFilasseEnPourcentage: null,
      poidsApporteEnTonne: null,
      codesGrainesRs3Apportes: [],
      isDuplicat: false,
      isUpdateModalOpen: false,
      rules: {
        required: value => (value !== null && value !== '') || 'Champ requis',
        positif: value => (value != null && value > 0) || 'Doit être > 0',
        max: value => this.ruleRendementMax(value),
        min: value => this.ruleRendementMin(value)
      }
    }
  },

  computed: {
    ...mapGetters({
      arborescenceTypeCulture: 'application/arborescenceTypeCulture',
      resumeParcellaire: 'refParcellaire/resumeParcellaire',
    }),
    listeCodeTypeCultureSelectionnes () {
      return this.cultures.map(culture => culture.typeCulture.code)
    },
    typeCultureSelected () {
      return this.arborescenceTypeCultureFiltree.map(typeCulture => trouverNoeud(typeCulture, this.typeCultureSelectedID)).find(typeCulture => typeCulture !== null)
    },
    arborescenceTypeCultureFiltree () {
      const cloneArborescence = Array.from(this.arborescenceTypeCulture)
      cloneArborescence.forEach(typeCulture => this.filtrerArborescence(typeCulture))
      return cloneArborescence
    },
    arborescenceTypeCultureFormatee () {
      const cloneArborescence = Array.from(this.arborescenceTypeCultureFiltree)
      return cloneArborescence.map(typeCulture => formaterNoeud(typeCulture, 0))
    },
    rendementMin () {
      if (this.typeCultureSelected) {
        if (this.isLinFibre && this.isSaisieFilasse) {
          return 10
        } else {
          const rendementMin = this.typeCultureSelected?.rendementMinEnQuintauxParHectare || 0
          return this.typeCultureSelected?.isUniteAfficheeTonneParHectare ? rendementMin * 0.1 : rendementMin
        }
      } else {
        return 10
      }
    },

    rendementMax () {
      if (this.typeCultureSelected) {
        if (this.isLinFibre && this.isSaisieFilasse) {
          return 10000
        } else {
          const rendementMax = this.typeCultureSelected?.rendementMaxEnQuintauxParHectare || 1500
          return this.typeCultureSelected?.isUniteAfficheeTonneParHectare ? rendementMax * 0.1 : rendementMax
        }
      } else {
        return 99999
      }
    },

    isLinFibre () {
      return this.typeCultureSelected && (this.typeCultureSelected.code === 'LFH' || this.typeCultureSelected.code === 'LFP')
    },
    nomCulture () {
      if (!this.typeCultureSelected) {
        return null
      }
      return this.typeCultureSelected.nom
    },
    titreModal () {
      return this.nomCulture != null ? this.nomCulture : 'Nouvelle culture'
    },
    graineRs3Correspondantes () {
      let stringListeGraineRs3 = ''
      this.typeCultureSelected?.graineRs3Correspondantes?.forEach(typeCulture => {
        this.codesGrainesRs3Apportes.forEach(resumeApportGraine => {
          if (resumeApportGraine === typeCulture.code) {
            stringListeGraineRs3 += typeCulture.libelle + ', '
          }
        })
      })
      //on enlève la dernière virgule
      return stringListeGraineRs3.substring(0, stringListeGraineRs3.length - 2)
    },
    isBtnContinuerStep1Disabled () {
      return this.typeCultureSelectedID === null || this.surface === null
    },
    isBtnContinuerStep2Disabled () {
      if (this.isLinFibre && this.isSaisieFilasse === null) {
        return true
      }
      if (this.rendement === null || this.poidsEnTonne === null) {
        return true
      }
      return this.rendement <= 0
    },
    isBtnContinuerStep3Disabled () {
      return this.tauxFilasseEnPourcentage === null
    },
    uniteRendement () {
      if (this.isLinFibre) {
        return this.isSaisieFilasse ? 'kg/ha' : 't/ha'
      } else if (this.typeCultureSelected?.isUniteAfficheeTonneParHectare) {
        return 't/ha'
      } else {
        return 'qx/ha'
      }
    },
    labelRendement () {
      return this.typeCultureSelected!= null && this.typeCultureSelected.code === 'MG' ? 'Rdt sec' : 'Rendement'
    },
    unitePoids () {
      if (this.isLinFibre) {
        return this.isSaisieFilasse ? 'kg' : 't'
      } else {
        return 't'
      }
    },
    labelPoids () {
      return this.typeCultureSelected != null && this.typeCultureSelected.code === 'MG' ? 'Poids total sec' : 'Poids total'
    },
  },

  created () {
    if (this.culture) {
      this.cultureModifiable = clone(this.culture)
      this.typeCultureSelectedID = this.culture.typeCulture.id
      this.surface = this.culture.surfaceEnHectare
      this.isSaisieFilasse = this.culture.isSaisieFilasse

      /*
       * La culture est stockee en paille. En saisie filasse, les champs de
       * l'ecran affichent de la filasse : la conversion est l'inverse exact de
       * celle appliquee a l'enregistrement, sans quoi rouvrir la modale divise
       * la valeur une seconde fois.
       */
      if (this.isLinFibre && this.isSaisieFilasse) {
        this.rendement = rendementFilasseEnKiloParHectare(this.culture.rendementEnQuintauxParHectare, this.culture.tauxFilasseEnPourcentage)
        this.poidsEnTonne = enKilo(poidsFilasseEnTonne(this.culture.poidsEnTonne, this.culture.tauxFilasseEnPourcentage))
      } else if (this.typeCultureSelected?.isUniteAfficheeTonneParHectare || this.isLinFibre) {
        this.rendement = this.culture.rendementEnQuintauxParHectare / 10
        this.poidsEnTonne = this.culture.poidsEnTonne
      } else {
        this.rendement = this.culture.rendementEnQuintauxParHectare
        this.poidsEnTonne = this.culture.poidsEnTonne
      }
      this.tauxFilasseEnPourcentage = this.culture.tauxFilasseEnPourcentage
      this.loadResumeApport()
    }

    this.preselectStep()
  },

  methods: {
    ...mapActions({
      getPoidsGraineConseilleCulture: 'culture/getPoidsGraineConseilleCulture',
      getResumeParcellaire: 'refParcellaire/getResumeParcellaire',
      resetResumeParcellaire: 'refParcellaire/reset',
      createOrUpdateCulture: 'culture/createOrUpdateCulture',
    }),
    ruleRendementMax (v) {
      return (v != null && v < this.rendementMax) || 'Doit être < ' + this.rendementMax
    },
    ruleRendementMin (v) {
      return (v != null && v > this.rendementMin) || 'Doit être > ' + this.rendementMin
    },
    /*
     * Rendement et poids sont toujours stockes en PAILLE, en quintaux par
     * hectare et en tonnes. Ces deux methodes sont le seul endroit qui traduit
     * la saisie de l'ecran vers ce format, et elles sont l'inverse exact de ce
     * que fait `created` a la relecture.
     */
    rendementAEnregistrer () {
      if (this.rendement == null) {
        return null
      }

      if (this.isLinFibre && this.isSaisieFilasse) {
        return rendementPailleEnQuintauxParHectare(this.rendement, this.tauxFilasseEnPourcentage)
      }

      if (this.typeCultureSelected?.isUniteAfficheeTonneParHectare || this.isLinFibre) {
        return this.rendement * 10
      }

      return this.rendement
    },

    poidsAEnregistrer () {
      if (this.poidsEnTonne == null) {
        return null
      }

      // En saisie filasse, le champ porte des KILOS de filasse malgre son nom.
      if (this.isLinFibre && this.isSaisieFilasse) {
        return poidsPailleEnTonne(enTonne(this.poidsEnTonne), this.tauxFilasseEnPourcentage)
      }

      return this.poidsEnTonne
    },

    saveCulture () {
      this.isDuplicat = false
      this.loading.ajoutCulture = true
      this.erreur.ajoutCulture = false

      this.cultureModifiable.isDonneesVerifiees = true
      this.cultureModifiable.typeCulture = this.typeCultureSelected
      this.cultureModifiable.surfaceEnHectare = this.surface
      this.cultureModifiable.rendementEnQuintauxParHectare = this.rendementAEnregistrer()
      this.cultureModifiable.poidsEnTonne = this.poidsAEnregistrer()
      
      this.cultureModifiable.tauxFilasseEnPourcentage = this.tauxFilasseEnPourcentage
      this.cultureModifiable.anneeRecolte = this.annee
      this.cultureModifiable.isSaisieFilasse = this.isSaisieFilasse

      this.createOrUpdateCulture(this.cultureModifiable)
        .then(() => {
          this.$emit('close')
        })
        .catch(err => {
          if (err.response.data.code === 'ALREADY_EXIST') {
            this.isDuplicat = true
          } else {
            this.erreur.ajoutCulture = true
          }
        })
        .finally(() => {
          this.loading.ajoutCulture = false
        })
    },
    onSaisirPlusTard () {
      this.isDuplicat = false
      this.loading.ajoutCulture = true
      this.erreur.ajoutCulture = false

      this.cultureModifiable.isDonneesVerifiees = false
      this.cultureModifiable.typeCulture = this.typeCultureSelected
      this.cultureModifiable.surfaceEnHectare = this.prioriseValeur(2, this.surface, this.cultureModifiable.surfaceEnHectare)
      this.cultureModifiable.isSaisieFilasse = this.isSaisieFilasse

      // Meme conversion qu'a l'enregistrement complet : une saisie partielle ne
      // doit pas etre stockee dans une autre unite qu'une saisie terminee.
      this.cultureModifiable.rendementEnQuintauxParHectare = this.prioriseValeur(3, this.rendementAEnregistrer(), this.cultureModifiable.rendementEnQuintauxParHectare)
      this.cultureModifiable.poidsEnTonne = this.prioriseValeur(3, this.poidsAEnregistrer(), this.cultureModifiable.poidsEnTonne)
      this.cultureModifiable.tauxFilasseEnPourcentage = this.isLinFibre ? this.prioriseValeur(4, this.tauxFilasseEnPourcentage, this.cultureModifiable.tauxFilasseEnPourcentage) : null
      this.cultureModifiable.anneeRecolte = this.annee

      this.createOrUpdateCulture(this.cultureModifiable)
        .then(() => this.$emit('close'))
        .catch(err => {
          if (err.response.data.code === 'ALREADY_EXIST') {
            this.isDuplicat = true
          } else {
            this.erreur.ajoutCulture = true
          }
        })
        .finally(() => {
          this.loading.ajoutCulture = false
        })
    },
    prioriseValeur (step, valeurModifiee, valeurOrigine) {
      if (Number(this.step) === step) {
        if (Number(valeurModifiee) === 0 || valeurModifiee === null) {
          return null
        } else {
          return valeurOrigine
        }
      } else {
        return valeurModifiee
      }

    },
    /*
     * Les valeurs affichees sont remises a zero avant l'appel : sans cela, le
     * temps de la reponse, l'ecran continue d'afficher celles de la graine
     * precedente, ce qui est plus trompeur qu'un simple delai.
     */
    loadResumeApport () {
      this.loading.resumeApport = true
      this.erreur.resumeApport = false
      this.poidsApporteEnTonne = null
      this.codesGrainesRs3Apportes = []

      this.getPoidsGraineConseilleCulture({
        anneeRecolte : this.annee,
        codeTypeCulture : this.typeCultureSelected?.code
      }).then(
        (result) => {
          this.poidsApporteEnTonne = result.poidsApporteEnTonne
          this.codesGrainesRs3Apportes = result.codesGrainesRs3Correspondants
        }
      )
        .catch(() => {this.erreur.resumeApport = true})
        .finally(() => this.loading.resumeApport = false)
    },
    loadResumeParcellaire () {
      this.loading.resumeParcellaire = true
      this.erreur.resumeParcellaire = false
      this.resetResumeParcellaire()

      this.getResumeParcellaire({
        anneeRecolte : this.annee,
        codeTypeCulture : this.typeCultureSelected?.code,
      })
        .catch(() => {this.erreur.resumeParcellaire = true})
        .finally(() => this.loading.resumeParcellaire = false)
    },

    changeStep (step) {
      if (step === 3) {
        if (this.isLinFibre) {
          this.currentStep = step
        } else {
          this.saveCulture()
        }
      } else {
        this.currentStep = step
      }
    },

    onClose () {
      this.$route.params.isModalCultureOpen = false
      this.$emit('close')
    },

    selectGraine (id) {
      this.typeCultureSelectedID = id
      if (this.culture !== null && this.typeCultureSelectedID !== null && this.culture.typeCulture.id !== id) {
        this.isUpdateModalOpen = true
      } else {
        this.cultureModifiable.typeCulture = this.typeCultureSelected
      }
      this.loadResumeApport()
      this.loadResumeParcellaire()
    },

    updateConfirm () {
      this.surface = null
      this.isSaisieFilasse = false
      this.rendement = null
      this.poidsEnTonne = null
      this.tauxFilasseEnPourcentage = null
      this.isUpdateModalOpen = false
    },
    cancelConfirm () {
      this.typeCultureSelectedID = this.culture ? this.culture.typeCulture.id : null
      this.isUpdateModalOpen = false
    },

    formatNumber (number, precision) {
      return numberFilter.formatNumber(number, precision)
    },
    computeRendement () {
      if (!this.lock.rendement) {
        this.lock.poids = true

        let rendementCalcule
        if (this.isLinFibre || this.typeCultureSelected?.isUniteAfficheeTonneParHectare) {
          rendementCalcule = (this.poidsEnTonne / this.surface)
        } else {
          rendementCalcule = ((this.poidsEnTonne / this.surface) * 10)
        }
        this.rendement = round(rendementCalcule, 3)
      }
      this.lock.rendement = false
    },

    computePoids () {
      if (!this.lock.poids) {
        this.lock.rendement = true

        let poidsCalcule
        if (this.isLinFibre || this.typeCultureSelected?.isUniteAfficheeTonneParHectare) {
          poidsCalcule = (this.rendement * this.surface)
        } else {
          poidsCalcule = ((this.rendement * this.surface) / 10)
        }
        this.poidsEnTonne = round(poidsCalcule, 3)
      }
      this.lock.poids = false
    },

    preselectStep () {
      if (this.typeCultureSelectedID && this.surface) {
        if (this.rendement && this.poidsEnTonne) {
          if (!this.isLinFibre || this.tauxFilasseEnPourcentage) {
            this.changeStep(this.stepParent)
          } else {
            this.changeStep(3)
          }
        } else {
          this.changeStep(2)
        }
      }
    },

    //ATTENTION: Méthodes récursives
    filtrerArborescence (noeudArborescence) {
      noeudArborescence.typeCultureFils.forEach(noeudFils => this.filtrerArborescence(noeudFils))

      //un noeud type de culture est désactivé:
      //SI tous ses fils le sont
      //OU le noeud est déjà selectionné
      //OU (le noeud a déjà été selectionné ET n'a pas le droit aux doublons)
      noeudArborescence.isDisabled =
        (noeudArborescence.typeCultureFils.length && noeudArborescence.typeCultureFils.every(noeudFils => noeudFils.isDisabled)) ||
        this.typeCultureSelectedID === noeudArborescence.id ||
        this.listeCodeTypeCultureSelectionnes.includes(noeudArborescence.code)
    },
  }
}
</script>

<style lang="scss" scoped>
.left-vertical-card {
  border-radius: 0 !important;
}

.bg-titre {
  height: 70px;
  background-color: #f1f2f5;
}

.input-number {
  width: 100%;
}
</style>
